import React from 'react';
import classNames from 'classnames';
import { GestureResponderEvent, Text, TouchableOpacity } from 'react-native';
import { Inline } from '@styleseat/ui';

import './RatingStars.scss';

const STAR_FILLED = 'pro-ratings-star';
const STAR_HALF = 'pro-ratings-star-half';
const STAR_EMPTY = 'pro-ratings-star-empty';

/**
 * Given an average rating (0-5) and star's index (1-5), determine how much the star should be
 * filled in.
 * @param {Number} rating - the average rating
 * @param {Number} index - the index of the star
 * @returns {string} CSS className
 */
function getFillClass(rating, index) {
  const numFullStars = Math.floor(rating);
  const remainder = rating - numFullStars;

  if (index <= numFullStars) {
    return STAR_FILLED;
  }

  if (index === numFullStars + 1) {
    if (remainder > 0.5) {
      // Anything >0.5 gets a full star
      return STAR_FILLED;
    }

    if (remainder > 0) {
      // Anything >0.0,<=0.5, gets a half star
      return STAR_HALF;
    }
  }
  return STAR_EMPTY;
}

export type RatingStarsProps = {
  ratingStars?: number,
  /** bigStars: (Boolean, Optional) specifies the size of the stars */
  bigStars?: boolean,
  /** onRatingChange: (Function, Optional) a callback that is
   * executed when the user clicks on a star. Can be omitted safely.
   */
  onRatingChange?: (newRating: number) => void,
  /**
   * size = the font size of the star glyph, defaults to 11.
   * if this prop is passed along with `bigStars` bigStars will
   * overwrite this prop and set the size to 45
   */
  size?: number,
  /**
   * fillColor defaults to punch
   */
  fillColor?: 'punch' | 'white' | 'yellow',
  /**
   * The number of star ratings. Include this prop if you want
   * to display the ratings summary information
   */
  ratingsCount?: number,
  /**
   * The number of written reviews. Include this prop if you want
   * to display the review summary information
   */
  reviewCount?: number,
  /** True to center the component in its container, otherwise false. Default is true */
  centered?: boolean,
  smallText?: boolean,
  /** True to show only a single star that is always filled */
  condensed?: boolean,
};
/**
 * RatingStars - a 1-5 star rating based on the passed parameter.
 *
 * Features:
 * - Can accept decimal numbers like 3.5 and will show partial stars
 * - Can accept an optional call back which allows this component to
 *   be used as an input for stars.
 */
export default class RatingStars extends React.Component<RatingStarsProps> {
  static defaultProps = {

  };

  /**
   * changeRating
   *
   * Determines new rating based on star that was clicked.
   */
  changeRating = (evt: GestureResponderEvent, rating: number) => {
    evt.stopPropagation();
    if (rating && this.props.onRatingChange) {
      this.props.onRatingChange(rating);
    }
  };

  /**
   * getSize returns the size (in px) used for font-size of the stars
   * @returns {*}
   */
  getSize = () => {
    if (this.props.bigStars) {
      return 45;
    }

    return this.props.size || 11;
  };

  render() {
    const {
      bigStars = false,
      smallText = false,
      fillColor = 'punch',
      ratingStars: rating = null,
      reviewCount = 0,
      ratingsCount = 0,
      centered = true,
      condensed = false,
    } = this.props;

    const size = this.getSize();

    const style = {
      fontSize: size,
      lineHeight: size,
    };

    let stars;

    if (condensed) {
      stars = [{
        index: 4,
        fillClass: STAR_FILLED,
      }];
    } else {
      stars = [1, 2, 3, 4, 5].map(index => ({
        index,
        fillClass: getFillClass(rating, index),
      }));
    }

    const classes = classNames(
      'pro-ratings-container',
      {
        white: fillColor === 'white',
        yellow: fillColor === 'yellow',
        'big-stars': bigStars,
        'small-text': smallText,
        'pro-ratings-container--centered': centered,
      },
    );
    return (
      <Inline
        testID="rating-stars"
        className={classes}
        space={2}
        alignItems="center"
      >
        {
          stars.map(star => (
            <TouchableOpacity
              onPress={evt => this.changeRating(evt, star.index)}
              accessibilityRole="button"
              key={star.index}
              disabled={!this.props.onRatingChange}
              testID={star.fillClass}
              data-rating={star.index}
            >
              <Text
                style={style}
                className={star.fillClass}
              />
            </TouchableOpacity>
          ))
        }
        {!!(ratingsCount || reviewCount || rating) && (
          <Inline space={2} className="review-count-summary">
            {!ratingsCount && !!reviewCount && (
              <Text className="review-count">
                ({reviewCount})
              </Text>
            )}
            {!!(ratingsCount || condensed) && !!rating && (
              <Text className="rating-number">
                {rating.toFixed(1)}
              </Text>
            )}
            {ratingsCount >= 5 && (
              <Text className="ratings-count">
                ({ratingsCount})
              </Text>
            )}
          </Inline>
        )}
      </Inline>
    );
  }
}
